import type { DmpProductKind } from '@/api/dmpV2.api';

const KEY = 'dmp.v2.purchase-intent';
const TTL = 30 * 60 * 1000;
const COOKIE = 'dmp_purchase_intent';
const CLEARED = 'dmp_purchase_cleared';
const cookie = (key: string): string | null => {
  try {
    const item = document.cookie.split('; ').find((v) => v.startsWith(key + '='));
    return item ? decodeURIComponent(item.slice(key.length + 1)) : null;
  } catch {
    return null;
  }
};
const setCookie = (key: string, value: string, seconds: number): void => {
  try {
    document.cookie = `${key}=${encodeURIComponent(value)}; Path=/; Max-Age=${seconds}; SameSite=Lax${
      window.location.protocol === 'https:' ? '; Secure' : ''
    }`;
  } catch {
    /* 網址傳遞仍可運作。 */
  }
};
const clearBackupFor = (intent: Pick<PurchaseIntent, 'product' | 'createdAt'>): void => {
  try {
    const backup = JSON.parse(cookie(COOKIE) || 'null');
    if (backup?.product === intent.product && backup?.createdAt === intent.createdAt) setCookie(COOKIE, '', 0);
  } catch {
    setCookie(COOKIE, '', 0);
  }
};
export interface PurchaseIntent {
  version: 1;
  product: string;
  kind: DmpProductKind;
  lang: 'en' | 'tc' | 'sc';
  createdAt: number;
  expiresAt: number;
  owner?: string;
}
const validCode = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-z][a-z0-9_]{0,79}$/.test(value);
export function clearPurchaseIntent(allTabs = false): void {
  let current: PurchaseIntent | null = null;
  try {
    try {
      current = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
    } catch {
      /* 仍需清除損壞的暫存。 */
    }
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* 禁用儲存時不保留選擇。 */
  }
  if (allTabs || !current) setCookie(COOKIE, '', 0);
  else clearBackupFor(current);
  if (allTabs) setCookie(CLEARED, String(Date.now()), 1800);
}
export function readPurchaseIntent(owner?: string, now = Date.now()): PurchaseIntent | null {
  try {
    let raw: string | null = null;
    try {
      raw = window.sessionStorage.getItem(KEY);
    } catch {
      /* 使用同站備援。 */
    }
    raw = raw || cookie(COOKIE);
    if (!raw) return null;
    const p = JSON.parse(raw) as PurchaseIntent;
    if (
      p.version !== 1 ||
      !validCode(p.product) ||
      !['plan', 'standalone_package', 'top_up'].includes(p.kind) ||
      !['en', 'tc', 'sc'].includes(p.lang) ||
      !Number.isSafeInteger(p.createdAt) ||
      p.createdAt > now ||
      p.expiresAt !== p.createdAt + TTL ||
      p.expiresAt <= now ||
      p.createdAt <= Number(cookie(CLEARED) || -1) ||
      (p.owner !== undefined && !/^[1-9][0-9]*$/.test(p.owner)) ||
      (owner && p.owner && p.owner !== owner)
    ) {
      clearPurchaseIntent();
      return null;
    }
    return p;
  } catch {
    clearPurchaseIntent();
    return null;
  }
}
export function savePurchaseIntent(
  product: string,
  kind: DmpProductKind,
  lang: string,
  owner?: string,
  now = Date.now()
): PurchaseIntent {
  if (!validCode(product) || !['plan', 'standalone_package', 'top_up'].includes(kind))
    throw new Error('Invalid product selection');
  const p: PurchaseIntent = {
    version: 1,
    product,
    kind,
    lang: lang === 'en' ? 'en' : lang === 'sc' ? 'sc' : 'tc',
    createdAt: now,
    expiresAt: now + TTL,
    ...(owner ? { owner } : {}),
  };
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* 儲存受限仍可依網址繼續。 */
  }
  // Cookie 僅保存訪客商品選擇，不保存帳號、報價或認證資訊。
  if (!owner) setCookie(COOKIE, JSON.stringify(p), Math.max(0, Math.ceil((p.expiresAt - Date.now()) / 1000)));
  else setCookie(COOKIE, '', 0);
  return p;
}
export function bindPurchaseOwner(owner: string): void {
  const p = readPurchaseIntent(owner);
  if (!p) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ ...p, owner }));
    clearBackupFor(p);
  } catch {
    clearPurchaseIntent();
  }
}
export function completePurchaseIntent(product: string, owner: string): void {
  if (readPurchaseIntent(owner)?.product === product) clearPurchaseIntent();
}
export function purchaseDestination(p: PurchaseIntent): string {
  return p.kind === 'plan'
    ? '/profile/plan-purchase?product=' + encodeURIComponent(p.product)
    : '/' + p.lang + '/Subscription?purchase=' + encodeURIComponent(p.product);
}
export function newRequestKey(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => n.toString(16).padStart(2, '0')).join('');
}
export function safeStripeUrl(value: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com' || url.port || url.username || url.password)
    throw new Error('Invalid checkout URL');
  return url.toString();
}
